<template>
  <b-card>
    <b-container>
      <b-row>
        <b-col md="12">
          <h4>Search Pickup and Dropoff</h4>
        </b-col>
        <b-col md="6">
          <LocationSearch @coordinates-caught="onPickupAddressSelected" />
        </b-col>
        <b-col md="6">
          <LocationSearch @coordinates-caught="onDropoffAddressSelected" />

        </b-col>
      </b-row>
      <h4>OR Select Pickup and Dropoff</h4>
      <div
        id="locationMap"
        style="width: 100%; height: 300px;"
      />

      <b-button
        class="my-2"
        variant="primary"
        :disabled="!isValid || loadingDistance"
        @click="fetchDistances"
      >
        <b-spinner
          v-if="loadingDistance"
          small
        />
        <span v-else>Get ETA</span>
      </b-button>

      <b-card
        v-if="compArray.length && !loadingDistance"
        class="mt-3"
      >
        <!-- Scrollable wrapper -->
        <div class="table-responsive">
          <table class="table table-bordered text-center align-middle">
            <thead>
              <tr>
                <th>Price Range</th>
                <th
                  v-for="category in serviceCategories"
                  :key="'category-header-' + category"
                  :colspan="3"
                  class="text-capitalize"
                >
                  {{ category }}
                </th>
              </tr>
              <tr>
                <th />
                <template v-for="category in serviceCategories">
                  <th
                    v-for="provider in providers"
                    :key="`header-${category}-${provider}`"
                    :class="`bg-${provider.toLowerCase()} text-white`"
                  >
                    {{ provider }}
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Distance (KM)</td>
                <template v-for="category in serviceCategories">
                  <td
                    v-for="provider in providers"
                    :key="`distance-${category}-${provider}`"
                  >
                    {{ getProviderDistance(provider) }}
                  </td>
                </template>
              </tr>
              <tr>
                <td>Duration (Mins)</td>
                <template v-for="category in serviceCategories">
                  <td
                    v-for="provider in providers"
                    :key="`duration-${category}-${provider}`"
                  >
                    {{ getProviderDuration(provider) }}
                  </td>
                </template>
              </tr>
              <tr>
                <td>Price Range</td>
                <template v-for="category in serviceCategories">
                  <td
                    v-for="provider in providers"
                    :key="`price-${category}-${provider}`"
                  >
                    {{ getPriceRange(provider, category) }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>

    </b-container>
  </b-card>
</template>

<script>
/* eslint-disable no-undef */
import {
  ref, reactive, computed, onMounted,
} from '@vue/composition-api'

import {
  BContainer,
  // BTable,
  BButton,
  BSpinner,
  BCard,
  BRow,
  BCol,
} from 'bootstrap-vue'
import axiosIns from '@/libs/axios'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import LocationSearch from './location-search.vue'

export default {
  components: {
    BContainer,
    // BTable,
    BButton,
    BSpinner,
    BCard,
    LocationSearch,
    BRow,
    BCol,
  },
  setup() {
    // Stores for the two points
    const pickup = ref(null)
    const dropoff = ref(null)
    const map = ref(null)
    const markers = reactive([])

    // List to hold API response
    const compArray = ref([])

    // Table fields
    const fields = [
      { key: 'name', label: 'Company' },
      { key: 'distance', label: 'Distance (Km)' },
      { key: 'duration', label: 'Duration (minutes)' },
      { key: 'estimates', label: 'Offers' },
    ]

    // Validation
    const isValid = computed(
      () => pickup.value && dropoff.value,
    )

    // Add marker to map
    const addMarker = (location, label) => {
      const marker = new google.maps.Marker({ position: location, map: map.value, label })
      markers.push(marker)
    }

    const removeMarkerByLabel = label => {
      const index = markers.findIndex(marker => marker.getLabel() === label)
      if (index !== -1) {
        markers[index].setMap(null)
        markers.splice(index, 1)
      }
    }
    const fitMapToMarkers = () => {
      if (markers.length === 0) return

      const bounds = new google.maps.LatLngBounds()
      markers.forEach(marker => bounds.extend(marker.getPosition()))

      if (markers.length === 1) {
        map.value.setCenter(bounds.getCenter())
        map.value.panTo(bounds.getCenter())
        map.value.setZoom(15)
      } else {
        map.value.fitBounds(bounds)
      }
    }
    const onPickupAddressSelected = coords => {
      pickup.value = coords
      removeMarkerByLabel('Pickup')
      addMarker(coords, 'Pickup')
      fitMapToMarkers()
    }
    const onDropoffAddressSelected = coords => {
      dropoff.value = coords
      removeMarkerByLabel('Dropoff')
      addMarker(coords, 'Dropoff')
      fitMapToMarkers()
    }
    // Handle map click
    const handleMapClick = e => {
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()

      if (!pickup.value) {
        // first click sets pickup
        pickup.value = { lat, lng }
        addMarker(e.latLng, 'Pickup')
      } else if (!dropoff.value) {
        // Second click sets dropoff
        dropoff.value = { lat, lng }
        addMarker(e.latLng, 'Dropoff')
      } else {
        // Reset first
        markers.forEach(marker => marker.setMap(null))
        markers.length = 0

        pickup.value = { lat, lng }
        dropoff.value = null

        addMarker(e.latLng, 'Pickup')
      }
      fitMapToMarkers()
    }

    // API call
    const loadingDistance = ref(false)
    const fetchDistances = async () => {
      try {
        loadingDistance.value = true
        const response = await axiosIns.get('/comp-eta', {
          params: {
            pickupLat: pickup.value.lat,
            pickupLng: pickup.value.lng,
            dropoffLat: dropoff.value.lat,
            dropoffLng: dropoff.value.lng,
          },
        })
        compArray.value = response.data.compArray
      } catch (error) {
        console.error(error)
      } finally {
        loadingDistance.value = false
      }
    }

    const serviceCategories = ['taxi', 'economy', 'economy plus', 'box']
    const providers = ['Jeeny', 'Careem', 'Petra']

    const getProviderData = name => compArray.value.find(item => item?.name?.toLowerCase() === name?.toLowerCase())

    const getProviderDistance = name => {
      const provider = getProviderData(name)
      return provider?.distance?.toFixed(2) || '-'
    }

    const getProviderDuration = name => {
      const provider = getProviderData(name)
      return provider?.duration?.toFixed(2) || '-'
    }

    const getPriceRange = (name, category) => {
      const provider = getProviderData(name)
      if (name === 'Jeeny') {
        if (category === 'taxi') {
          const estimate = provider?.estimates?.find(
            est => est?.service?.toLowerCase() === 'regular',
          )
          return estimate?.priceRange || '-'
        }
        if (category === 'economy plus') {
          const estimate = provider?.estimates?.find(
            est => est?.service?.toLowerCase() === 'xpress',
          )
          return estimate?.priceRange || '-'
        }

        const estimate = provider?.estimates?.find(
          est => est?.service?.toLowerCase() === category?.toLowerCase(),
        )
        return estimate?.priceRange || '-'
      }
      const estimate = provider?.estimates?.find(
        est => est?.service?.toLowerCase() === category?.toLowerCase(),
      )
      return estimate?.priceRange || '-'
    }

    // Initialize the map
    onMounted(() => {
      const center = { lat: 31.963158, lng: 35.930359 }
      map.value = new google.maps.Map(
        document.getElementById('locationMap'),
        {
          zoom: 15,
          center,
        },
      )

      map.value.addListener('click', handleMapClick)
    })

    return {
      serviceCategories,
      getProviderDuration,
      getProviderDistance,
      getPriceRange,
      providers,
      isValid,
      snakeCaseToCamelCase,
      fetchDistances,
      compArray,
      loadingDistance,
      fields,
      onDropoffAddressSelected,
      pickup,
      dropoff,
      onPickupAddressSelected,
    }
  },
}

</script>

  <style scoped>
  #locationMap {
    width: 100%;
    height: 500px;
  }
.bg-jeeny {
  background-color: #ec008c !important;
}
.bg-careem {
  background-color: #00c18c !important;
}
.bg-petra {
  background-color: #0055a5 !important;
}
.text-white {
  color: #fff !important;
}
td {
  white-space: nowrap;
}
</style>
